import type { BoxCollider, FlatFloor, FloorRegion } from './collision';
import { PLAYER_RADIUS } from './controller';

/**
 * The bodega interior — S05 scope ("doors + bodega interior scene",
 * PLAN-CITY §5). Placed at its own isolated origin, far from the exterior
 * block, rather than carved into `BUILDINGS.building_a`'s own footprint:
 * the exterior south wall (`buildingWalls()` in layout.ts) is already a
 * full, reviewed, load-bearing solid span, and `BODEGA_DOOR` is itself
 * already a solid collider layered on top of it (S01's own comment: "solid
 * for S01 — no interior yet"). Carving a real gap into that wall risks
 * reopening the exact class of collision bug §0.4/collision.ts's own
 * comments spent three review rounds closing (S01's wall-skip-through
 * family). Since every scene transition in this game is already
 * teleport-based (spawn, respawn, the dev-tap teleport), a teleport-based
 * door is the established idiom, not a new one — `main.ts` wires
 * `world/interact.ts`'s `bodega_enter`/`bodega_exit` actions to
 * `player.teleport()` + `orbitCam.snapTo()`, exactly like every other
 * transition already does. The exterior collider/visual are UNCHANGED.
 *
 * `INTERIOR_ORIGIN` (x=140) sits safely outside every other system's own
 * bounded area — `NavGrid`'s BOUNDS (x -30..30), every `ai/habitats.ts`
 * zone, and the new S05 boundary ring (x -38..38) — so this room can never
 * be reached except by the explicit teleport, and never interferes with
 * enemy pathing/spawning/perimeter checks (all of which only ever query
 * points within their own much smaller ranges).
 */
export const INTERIOR_ORIGIN = { x: 140, z: 0 };

// A small room, not a full shop floor — S05 is "world systems" (can you
// walk in and out), not "loop content" (buy/sell/craft is S10's explicit
// scope, PLAN-CITY §5: "bodega home base v1 (heal, stash, craft bench)").
const HALF_WIDTH = 3.5; // X
const HALF_DEPTH = 3; // Z
export const INTERIOR_ROOM = {
  minX: INTERIOR_ORIGIN.x - HALF_WIDTH,
  maxX: INTERIOR_ORIGIN.x + HALF_WIDTH,
  minZ: INTERIOR_ORIGIN.z - HALF_DEPTH,
  maxZ: INTERIOR_ORIGIN.z + HALF_DEPTH,
  height: 2.6, // a touch above PLAYER_TOTAL_HEIGHT (1.4) — a cozy interior ceiling, not a building-scale one
};

/**
 * S05 review finding (HIGH, live-reproduced): OrbitCamera's default
 * outdoor distance (5.5m) + pitch (0.55rad, core/camera.ts) climbs ~0.61m
 * of height per 1m of horizontal travel — over this room's own real wall
 * distances (3–4.5m from `INTERIOR_SPAWN`) the ray always climbs above the
 * 2.6m walls before reaching one, so the occlusion raycast never hits
 * anything and the camera resolves outside/above the room in every
 * direction, every time. main.ts passes this as `maxDistance` to
 * `orbitCam.update()`/`snapTo()` while the player is inside — at pitch
 * 0.55, 2.6m of pull-back puts the camera ~2.36m up (1.0 pivot height +
 * 2.6×sin(0.55)) and ~2.22m out horizontally (2.6×cos(0.55)), comfortably
 * under the 2.6m ceiling and inside the room's own 3–3.5m half-extents —
 * derived from the room's OWN height, not a guessed constant, so a future
 * resize of INTERIOR_ROOM.height is the one number that needs re-checking.
 */
export const INTERIOR_CAMERA_MAX_DISTANCE = 2.6;

/** Where the player lands after interacting with the exterior `BODEGA_DOOR` — just inside the near (south) wall, facing into the room. */
export const INTERIOR_SPAWN = { x: INTERIOR_ORIGIN.x, z: INTERIOR_ROOM.maxZ - 1.5 };

/** The EXIT interactable — deliberately close to `INTERIOR_SPAWN` (a real bodega's door and register both sit right by the entrance), so entering and immediately leaving reads as a real vestibule, not a maze. */
export const INTERIOR_EXIT_MARKER = { x: INTERIOR_ORIGIN.x, z: INTERIOR_ROOM.maxZ - 0.6 };

/** A counter prop toward the back of the room — gray-box dressing (§0.4), not a functional stash/register (S10's job). */
export const INTERIOR_COUNTER = {
  x: INTERIOR_ORIGIN.x,
  z: INTERIOR_ROOM.minZ + 1.1,
  width: 3.2,
  depth: 0.7,
  height: 0.95,
  color: 0x6b5a45,
};

export const INTERIOR_FLOOR: FloorRegion = {
  kind: 'flat',
  minX: INTERIOR_ROOM.minX,
  maxX: INTERIOR_ROOM.maxX,
  minZ: INTERIOR_ROOM.minZ,
  maxZ: INTERIOR_ROOM.maxZ,
  y: 0,
  // Explicit, not the default — this floor's XZ footprint is unique to the
  // isolated interior origin, so it can never actually tie against another
  // region the way `STREET_FLOOR`'s priority-0 fallback needs to defer to
  // ramps/stairs (collision.ts's own `DEFAULT_PRIORITY` comment). Stated
  // for the same reason every other named FloorRegion in this project
  // states it: so a future region added here later doesn't have to
  // re-derive whether this one was deliberate.
  priority: 1,
};

/** `interiorWalls()`'s own south-doorway gap half-width — hoisted so `INTERIOR_STOOP_FLOOR` below always matches the gap it's catching exactly, never a hand-copied guess (Classic gotcha #19's formula-drift lesson). */
const DOOR_HALF_WIDTH = 1.1;

/** Four solid walls enclosing the room, same `buildingWalls()`-style segment idiom as layout.ts (a real threshold gap on the south face — where the entry/exit interactables sit — rather than a solid panel there, since this room genuinely is walked through, not just teleported past). */
export function interiorWalls(): BoxCollider[] {
  const t = 0.4;
  const r = INTERIOR_ROOM;
  const doorCenterX = INTERIOR_ORIGIN.x;
  return [
    { minX: r.minX, maxX: r.maxX, minZ: r.minZ, maxZ: r.minZ + t, topY: r.height, bottomY: 0 }, // north (back)
    // south (front/entry) — split around the doorway gap, mirroring
    // `buildingWalls()`'s doorway-as-omitted-segment idiom exactly.
    { minX: r.minX, maxX: doorCenterX - DOOR_HALF_WIDTH, minZ: r.maxZ - t, maxZ: r.maxZ, topY: r.height, bottomY: 0 },
    { minX: doorCenterX + DOOR_HALF_WIDTH, maxX: r.maxX, minZ: r.maxZ - t, maxZ: r.maxZ, topY: r.height, bottomY: 0 },
    { minX: r.minX, maxX: r.minX + t, minZ: r.minZ, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // west
    { minX: r.maxX - t, maxX: r.maxX, minZ: r.minZ, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // east
  ];
}

/**
 * T3-1 (STATUS-CITY.md 2026-08-26 item 13): the south doorway above leaves a
 * real, walkable gap — but BOTH the enter/exit transitions are already
 * 100% teleport-driven (world/interact.ts's `enter_bodega`/`exit_bodega`),
 * so nothing ever REQUIRES walking through it, and nothing stopped a player
 * who did anyway. `INTERIOR_ORIGIN` sits fully isolated (this file's own
 * header comment) — there is NO floor of any kind south of
 * `INTERIOR_ROOM.maxZ`, so simply walking toward the doorway (instead of
 * pressing EXIT BODEGA) stepped off a cliff into ~100m of open air, free-
 * falling until `PlayerCapsule`'s -20m safety net recovered the player back
 * at `SPAWN_POSITION` — this, live-verified, is what a G2 playtest report
 * ("visibly falling... it's almost like it's built in the sky") actually
 * was, NOT a landing-height bug in the interact-triggered teleport itself
 * (that already lands flush at street level — confirmed live, see
 * STATUS-CITY.md's T3 row). This small flat patch — sized to the doorway's
 * own gap width plus a small margin, reaching past
 * `INTERIOR_EXIT_MARKER`'s own trigger radius (2.4 ± 1.6 = z0.8-4.0) — turns
 * "walk off a cliff" into "stand on a small stoop, in easy reach of the
 * EXIT BODEGA prompt," without touching the doorway's collision gap itself
 * (so the delivered `blk_bodega_interior.glb`'s own matching visual opening
 * — block.ts's `attachInterior()` comment — stays correct).
 */
export const INTERIOR_STOOP_FLOOR: FlatFloor = {
  kind: 'flat',
  minX: INTERIOR_ORIGIN.x - DOOR_HALF_WIDTH - 0.2,
  maxX: INTERIOR_ORIGIN.x + DOOR_HALF_WIDTH + 0.2,
  minZ: INTERIOR_ROOM.maxZ,
  maxZ: INTERIOR_ROOM.maxZ + 1.5,
  y: 0,
  priority: 1,
};

/**
 * T3-1 follow-up (Karan's post-T3 phone check, 2026-08-26: "you can't really
 * just walk right out the door — you kind of fall out the door"): crossing
 * this threshold now IS the exit — main.ts's tick performs the same
 * teleport-to-`BODEGA_EXTERIOR_STANDPOINT` the EXIT BODEGA button does, the
 * same tick the player's center clears the south wall's outer face
 * (`INTERIOR_ROOM.maxZ`), so walking out the doorway seamlessly lands on the
 * sidewalk. This is what closed the two gaps the stoop+curb version still
 * had in real play: the stoop's own EAST/WEST edges were open (veer slightly
 * while walking out → step off the side → the -20m safety net "fall"), and a
 * player pressed against the curb (center z≈4.15) sat OUTSIDE
 * `bodega_exit`'s own trigger circle (z 0.8–4.0), so the natural walk-out
 * ended at an invisible dead end with no prompt visible. The x-band is the
 * stoop's own span plus the real capsule radius (PLAYER_RADIUS, imported —
 * never a hand-copied literal, per this file's own DOOR_HALF_WIDTH
 * precedent / Classic gotcha #19) plus a small deliberate margin, so the
 * whole doorway gap — including its corners — is covered; nothing else
 * inside the room can satisfy `z > INTERIOR_ROOM.maxZ` (every other span of
 * the south wall is solid).
 */
const DOORWAY_BAND_MARGIN = PLAYER_RADIUS + 0.05;
export function isPastDoorwayThreshold(x: number, z: number): boolean {
  return (
    z > INTERIOR_ROOM.maxZ &&
    x > INTERIOR_STOOP_FLOOR.minX - DOORWAY_BAND_MARGIN &&
    x < INTERIOR_STOOP_FLOOR.maxX + DOORWAY_BAND_MARGIN
  );
}

/**
 * T3-1 review fix (MEDIUM, dont-break-list lens, CONFIRMED): the stoop above
 * is deliberately open at its own south edge ("a catch, not a wall" — its
 * own comment) — a player who reaches the stoop but doesn't stop to tap
 * EXIT BODEGA (exactly the failure mode this whole fix exists to close —
 * "nothing ever REQUIRES walking through [the doorway], and nothing stopped
 * a player who did anyway") just keeps walking 1.5m further and falls off
 * THIS edge instead, reproducing the identical reported symptom on a delay,
 * not eliminating it. Unlike the doorway gap itself (which must stay open to
 * match the delivered `blk_bodega_interior.glb`'s own visual opening — see
 * `interiorWalls()`'s comment), there is no delivered geometry at the
 * stoop's own outer edge to visually contradict — `INTERIOR_ORIGIN` is fully
 * isolated, so an invisible collider here conflicts with nothing. Same
 * "invisible wall extension past the themed prop" idiom `layout.ts`'s
 * `CORONA_EXIT_BARRIER` already establishes for this exact class of seam.
 * Post-follow-up role: `isPastDoorwayThreshold` above fires before a player
 * can ever STAND here, so the stoop + this curb are now pure backstop (they
 * keep the physics sound if the auto-exit is ever bypassed), not the primary
 * fix.
 */
export function interiorStoopCurb(): BoxCollider {
  return {
    minX: INTERIOR_STOOP_FLOOR.minX,
    maxX: INTERIOR_STOOP_FLOOR.maxX,
    minZ: INTERIOR_STOOP_FLOOR.maxZ,
    maxZ: INTERIOR_STOOP_FLOOR.maxZ + 0.4,
    topY: 1.6, // clears PLAYER_TOTAL_HEIGHT (1.4) with margin, matching allWalls()'s own subway-side-wall topY convention
    bottomY: 0,
  };
}

/**
 * The counter as a solid obstacle — 0.95m sits inside `VAULT_MAX_HEIGHT`
 * (1.1m, controller.ts), so `findVaultLedge` treats it exactly like a park
 * bench: a real, deliberate ledge, not a wall the player must walk around
 * in a 6×7m room. Same `PARK_PROPS`-style "short prop = vault target"
 * design already established in layout.ts, applied here for the same
 * reason (nothing to actively re-derive; this IS the pattern).
 */
export function interiorCounterCollider(): BoxCollider {
  const c = INTERIOR_COUNTER;
  return {
    minX: c.x - c.width / 2,
    maxX: c.x + c.width / 2,
    minZ: c.z - c.depth / 2,
    maxZ: c.z + c.depth / 2,
    topY: c.height,
    bottomY: 0,
  };
}

/**
 * The counter's OWN top surface as a real floor region — layout.ts's
 * `PARK_PROP_TOPS` comment documents exactly why this is required, not
 * optional, for any vaultable prop: without it, landing on top of the
 * counter finds no floor region there, falls straight back through the
 * collider `interiorCounterCollider()` defines, and immediately
 * re-triggers the vault it just finished — an infinite vault/fall
 * oscillation. block.ts must add this to `FLOORS` alongside
 * `INTERIOR_FLOOR`, the same way layout.ts's own `FLOORS` array already
 * includes both `STREET_FLOOR` and every `PARK_PROP_TOPS` entry together.
 */
export function interiorCounterTopFloor(): FloorRegion {
  const c = INTERIOR_COUNTER;
  return {
    kind: 'flat',
    minX: c.x - c.width / 2,
    maxX: c.x + c.width / 2,
    minZ: c.z - c.depth / 2,
    maxZ: c.z + c.depth / 2,
    y: c.height,
  };
}

// --- S10: bodega home base v1 (PLAN-CITY §5: "heal, stash, craft bench") ---
// Room bounds: x[136.5,143.5] z[-3,3] (INTERIOR_ROOM above). The counter
// cluster (INTERIOR_COUNTER, x140 z≈-1.9, width 3.2 -> x[138.4,141.6]) and
// the entry/exit cluster (x140, z[1.5,2.4]) both sit on the room's own
// centerline — heal/craft flank them along the side walls (0.4m thick, per
// interiorWalls()) with real clearance, and stash reuses the counter's own
// position (interiorCounterCollider()'s own comment already named it "not a
// functional stash/register — S10's job").
export const HEAL_STATION = { x: 137.3, z: 0.5 };
export const CRAFT_BENCH = { x: 142.7, z: 0.5 };
/** A standing point just south of the counter (matching every other "stand outside the thing you interact with" marker in this game — BODEGA_EXTERIOR_STANDPOINT, layout.ts), not the counter's own center, which sits inside its solid collider. */
export const STASH_STANDPOINT = { x: INTERIOR_COUNTER.x, z: INTERIOR_COUNTER.z + INTERIOR_COUNTER.depth / 2 + 0.85 };

// --- S11: bodega hunt board (PLAN-CITY §5: "bodega hunt board") ---
/**
 * A back-west corner, west of the counter's own x-range ([138.4,141.6]) and
 * well clear of HEAL_STATION/CRAFT_BENCH/STASH_STANDPOINT/bodega_exit's own
 * trigger radii (checked by hand against all four: nearest is HEAL_STATION,
 * ~2.7m away vs. a combined radius of 2.4m — 0.3m real margin, matching this
 * project's own established "≥0.2m" bar from the S10 review's STASH fix).
 * 0.15m clear of the west wall (interiorWalls(), x<136.9) — tighter than most
 * markers in this room but the room itself has no space left to spare; the
 * player-facing trigger only needs to be reachable from the room-interior
 * side, which this is.
 */
export const HUNT_BOARD = { x: 137.15, z: -2.2 };

// --- Wave L-2 (PLAN-CITY2 §6): bodega wardrobe stand ---
/**
 * The back-EAST corner — mirrors HUNT_BOARD's own back-west corner exactly
 * (same z, same distance-from-wall reasoning: 0.25m clear of the east wall,
 * x<143.1). Checked by hand against every other marker in this room: nearest
 * is CRAFT_BENCH (142.7, 0.5), distance ≈2.70m vs. a combined trigger radius
 * of 2.4m (CRAFT_BENCH's own 1.4 + this marker's 1.0, see world/interact.ts)
 * — 0.3m real margin, matching the ≥0.2m bar the S10 review established for
 * STASH_STANDPOINT's own overlap fix. HUNT_BOARD itself is 5.7m away.
 */
export const WARDROBE_STAND = { x: 142.85, z: -2.2 };
